import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useCompanyInfo } from '@/hooks/useCompanyInfo';

// Phase 1 section keys - MUST match OnboardingStatusCard SECTION_CONFIG and OnboardingContext SECTION_KEYS
const PHASE1_SECTION_KEYS = [
  'business_profile',
  'bank_accounts',
  'inventory_list_setup',
  'vendors',
  'prep_menu_recipes',
  'pos_integration',
  'payroll',
] as const;

// Phase 2 section keys - keep in sync with OnboardingStatusCard
const PHASE2_SECTION_KEYS = [
  'bank_accounts_phase2',
  'cogs_vendor_training',
  'pos_reports',
] as const;

export function useAllOnboardingComplete(): {
  phase1Complete: boolean;
  allOnboardingComplete: boolean;
  isLoading: boolean;
} {
  const { data: companyData } = useCompanyInfo();
  const companyId = companyData?.id;

  const query = useQuery({
    queryKey: ['all-onboarding-complete', companyId],
    queryFn: async (): Promise<{ phase1Complete: boolean; allOnboardingComplete: boolean }> => {
      if (!companyId) return { phase1Complete: false, allOnboardingComplete: false };

      const { data: progressData } = await supabase
        .from('onboarding_progress')
        .select('sections_completed, completed_at')
        .eq('company_id', companyId)
        .single();

      const sectionsCompleted = (progressData?.sections_completed as Record<string, unknown>) || {};
      const phase2Data = (sectionsCompleted.phase2 as Record<string, { status?: string }>) || {};

      // Phase 1 complete: completed_at set or all Phase 1 sections done
      const phase1AllDone = PHASE1_SECTION_KEYS.every((key) => {
        const section = sectionsCompleted[key] as { status?: string } | undefined;
        return section && (section.status === 'completed' || section.status === 'skipped');
      });
      const phase1MarkedComplete = !!progressData?.completed_at;
      const phase1Complete = phase1MarkedComplete || phase1AllDone;

      // Phase 2 complete: all Phase 2 sections completed
      const phase2CompletedCount = PHASE2_SECTION_KEYS.filter((key) => {
        const section = phase2Data[key];
        return section && section.status === 'completed';
      }).length;
      const isPhase2Complete = phase2CompletedCount === PHASE2_SECTION_KEYS.length;
      const allOnboardingComplete = phase1Complete && isPhase2Complete;

      return { phase1Complete, allOnboardingComplete };
    },
    enabled: !!companyId,
    staleTime: 2 * 60 * 1000,
  });

  const result = query.data ?? { phase1Complete: false, allOnboardingComplete: false };
  return {
    phase1Complete: result.phase1Complete,
    allOnboardingComplete: result.allOnboardingComplete,
    isLoading: query.isLoading,
  };
}
